import { useState, useCallback } from "react";
import { apiService } from "../services/apiClient";
import { apiUrl } from "../services/apiUrl";

export const useRoleAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const [currentRole, setCurrentRole] = useState(null);

  // Fetch all roles
  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.roles());
      setRoles(response.data.data || []);
      return response.data.data || [];
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching roles:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch role by ID
  const fetchRoleById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.roleById(id));
      setCurrentRole(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching role:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new role
  const createRole = useCallback(async (roleData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(
        apiUrl.createRole(),
        roleData
      );
      setRoles((prevRoles) => [...prevRoles, response.data]);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error creating role:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update role
  const updateRole = useCallback(async (id, roleData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.put(
        apiUrl.updateRole(id),
        roleData
      );
      setRoles((prevRoles) =>
        prevRoles.map((r) => (r.id === id ? response.data : r))
      );
      if (currentRole?.id === id) {
        setCurrentRole(response.data);
      }
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error updating role:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentRole]);

  // Delete role
  const deleteRole = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await apiService.delete(apiUrl.deleteRole(id));
      setRoles((prevRoles) =>
        prevRoles.filter((r) => r.id !== id)
      );
      if (currentRole?.id === id) {
        setCurrentRole(null);
      }
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error deleting role:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentRole]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear current role
  const clearCurrentRole = useCallback(() => {
    setCurrentRole(null);
  }, []);

  return {
    loading,
    error,
    roles,
    currentRole,
    fetchRoles,
    fetchRoleById,
    createRole,
    updateRole,
    deleteRole,
    clearError,
    clearCurrentRole,
  };
};

export default useRoleAdmin;
